import React from 'react';
import { Box, Button } from '@mui/material';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { ReportingFilters } from '../../../utils/types/reporting/reporting_types';
import { CustomSelect } from '../../custom_react_select/custom_select';
import { DatePicker } from '../../input/date_picker/date_picker';

export interface ReportingFilterBarProps {
  filters: ReportingFilters;
  onChange: (filters: ReportingFilters) => void;
  departments: string[];
  jobTitles: string[];
}

const toOptions = (values: string[]): SelectOptions[] =>
  values.map((value) => ({ value, label: value }));

export const ReportingFilterBar: React.FC<ReportingFilterBarProps> = ({
  filters,
  onChange,
  departments,
  jobTitles,
}) => {
  const departmentOptions = toOptions(departments);
  const jobTitleOptions = toOptions(jobTitles);

  const handleDateChange = (range: {
    startDate: string | null;
    endDate: string | null;
  }) => {
    onChange({
      ...filters,
      dateRange: {
        startDate: range.startDate ? new Date(range.startDate) : null,
        endDate: range.endDate ? new Date(range.endDate) : null,
      },
    });
  };

  const handleDepartmentChange = (selected: SelectOptions[] | null) => {
    onChange({
      ...filters,
      departments: selected ? selected.map((option) => option.value) : [],
    });
  };

  const handleJobTitleChange = (selected: SelectOptions[] | null) => {
    onChange({
      ...filters,
      jobTitles: selected ? selected.map((option) => option.value) : [],
    });
  };

  const handleClear = () => {
    onChange({
      dateRange: { startDate: null, endDate: null },
      departments: [],
      jobTitles: [],
      locations: [],
    });
  };

  const hasActiveFilters =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.locations.length > 0 ||
    !!filters.dateRange.startDate ||
    !!filters.dateRange.endDate;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: { xs: 2, sm: 3 },
        py: 2,
      }}
    >
      <Box sx={{ width: { xs: '100%', sm: 220 } }}>
        <DatePicker
          label="Date Range"
          startDate={filters.dateRange.startDate}
          endDate={filters.dateRange.endDate}
          onChange={handleDateChange}
        />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 220 } }}>
        <CustomSelect
          name="departments"
          label="Department"
          placeholder="All departments"
          isMulti
          clearable
          options={departmentOptions}
          value={departmentOptions.filter((option) =>
            filters.departments.includes(option.value),
          )}
          onChange={handleDepartmentChange}
        />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 220 } }}>
        <CustomSelect
          name="jobTitles"
          label="Job Title"
          placeholder="All job titles"
          isMulti
          clearable
          options={jobTitleOptions}
          value={jobTitleOptions.filter((option) =>
            filters.jobTitles.includes(option.value),
          )}
          onChange={handleJobTitleChange}
        />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 'auto' }, ml: { sm: 'auto' }, pb: 0.5 }}>
        <Button
          variant="text"
          size="small"
          onClick={handleClear}
          disabled={!hasActiveFilters}
          sx={{ fontWeight: 600, color: 'text.secondary' }}
        >
          Clear filters
        </Button>
      </Box>
    </Box>
  );
};
